import {
  isCancellable,
  isCancellableWhileAwaiting,
  isCoachConfirmable,
  isJoinable,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-capabilities';
import {
  incursPenalty,
  isFuture,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-dates';
import type {
  CoachingConfig,
  CoachingSession,
  CoachProfile,
} from '@efdigital/ll-addon-data';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { cell, resource, use } from 'ember-resources';

import { CancelSessionModal } from './session-action-modals/cancel-session.gts';
import { ConfirmAttendanceModal } from './session-action-modals/confirm-attendance.gts';
import { ConfirmBookingModal } from './session-action-modals/confirm-booking.gts';
import { DeclineBookingModal } from './session-action-modals/decline-booking.gts';

interface SessionActionsSignature {
  Args: {
    session: CoachingSession;
    coach: CoachProfile;
    coachingConfig: CoachingConfig;
    buttonClass?: string;
    hideCancel?: boolean;
    onChanged?: () => void | Promise<void>;
  };
  Element: HTMLDivElement;
}

type CurrentStatus = {
  canReschedule: boolean;
  canCancel: boolean;
  isJoinable: boolean;
  isCoachConfirmable: boolean;
  incursPenalty: boolean;
};

function statusEquals(status1: CurrentStatus, status2: CurrentStatus): boolean {
  return (
    status1.canReschedule === status2.canReschedule &&
    status1.canCancel === status2.canCancel &&
    status1.isJoinable === status2.isJoinable &&
    status1.isCoachConfirmable === status2.isCoachConfirmable &&
    status1.incursPenalty === status2.incursPenalty
  );
}

export class SessionActions extends Component<SessionActionsSignature> {
  @tracked loading = false;

  get canMarkAttendance(): boolean {
    return (
      this.args.session.status === 'completed' && !this.args.session.coachReport
    );
  }

  get showCancelButton(): boolean {
    return (
      this.currentStatus.canCancel && !this.currentStatus.isCoachConfirmable
    );
  }

  calculateStatus() {
    const { status } = this.args.session;
    const awaitingConfirmation = isCancellableWhileAwaiting(status);

    return {
      canReschedule: isFuture(this.args.session) && status === 'scheduled',
      canCancel:
        awaitingConfirmation ||
        (isFuture(this.args.session) && isCancellable(status)),
      isJoinable: isJoinable(status),
      isCoachConfirmable: isCoachConfirmable(status),
      incursPenalty: awaitingConfirmation
        ? false
        : incursPenalty(this.args.session, this.args.coachingConfig),
    };
  }

  @use currentStatus = resource<CurrentStatus>(({ on }) => {
    const status = cell(this.calculateStatus());

    const timer = setInterval(() => {
      const newStatus = this.calculateStatus();
      if (!statusEquals(status.current, newStatus)) {
        status.set(newStatus);
      }
    }, 60 * 1000);

    on.cleanup(() => clearInterval(timer));

    return () => status.current;
  });

  <template>
    <div class="d-flex align-items-center gap-2 flex-wrap" ...attributes>
      {{#if this.currentStatus.isCoachConfirmable}}
        <ConfirmBookingModal
          @session={{@session}}
          @onChanged={{@onChanged}}
          as |showConfirmModal|
        >
          <button
            class="btn btn-primary {{@buttonClass}}"
            type="button"
            {{on "click" showConfirmModal}}
          ><iconify-icon inline icon="ph:check-bold" aria-hidden="true" />
            Accept booking</button>
        </ConfirmBookingModal>

        <DeclineBookingModal
          @session={{@session}}
          @onChanged={{@onChanged}}
          as |showDeclineModal|
        >
          <button
            class="btn btn-outline-primary {{@buttonClass}}"
            type="button"
            {{on "click" showDeclineModal}}
          ><iconify-icon inline icon="ph:x-bold" aria-hidden="true" />
            Decline</button>
        </DeclineBookingModal>
      {{/if}}

      {{#if this.currentStatus.isJoinable}}
        <p class="small text-body-secondary mb-0">
          <iconify-icon inline icon="ph:video-camera" aria-hidden="true" />
          Join from your calendar invite
        </p>
      {{/if}}

      {{!--  TODO: Rescheduling is sugar for cancel + book;
      {{#if this.currentStatus.canReschedule}}
        {{#if (incursPenalty @session @coachingConfig)}}
          <button
            class="btn btn-outline-danger {{@buttonClass}}"
            type="button"
          ><iconify-icon inline icon="ph:warning-circle" aria-hidden="true" />
            Reschedule</button>
        {{else}}
          <button
            class="btn btn-outline-primary {{@buttonClass}}"
            type="button"
          ><iconify-icon inline icon="ph:clock" aria-hidden="true" />
            Reschedule</button>
        {{/if}}
      {{/if}}
      --}}

      {{#if this.showCancelButton}}
        {{#unless @hideCancel}}
          <CancelSessionModal
            @session={{@session}}
            @incursPenalty={{this.currentStatus.incursPenalty}}
            @onChanged={{@onChanged}}
            as |showModal|
          >
            {{#if this.currentStatus.incursPenalty}}
              <button
                class="btn btn-outline-danger {{@buttonClass}}"
                type="button"
                {{on "click" showModal}}
              ><iconify-icon
                  inline
                  icon="ph:warning-circle"
                  aria-hidden="true"
                />
                Cancel</button>
            {{else}}
              <button
                class="btn btn-outline-primary {{@buttonClass}}"
                type="button"
                {{on "click" showModal}}
              ><iconify-icon inline icon="ph:x" aria-hidden="true" />
                Cancel</button>
            {{/if}}
          </CancelSessionModal>
        {{/unless}}
      {{/if}}

      {{#if this.canMarkAttendance}}
        <ConfirmAttendanceModal
          @session={{@session}}
          @onChanged={{@onChanged}}
          as |showModal|
        >
          <button
            class="btn btn-outline-primary {{@buttonClass}}"
            type="button"
            {{on "click" showModal}}
          >Report session outcome
            <iconify-icon inline icon="ph:clipboard-text" aria-hidden="true" />
          </button>
        </ConfirmAttendanceModal>
      {{/if}}
    </div>
  </template>
}
